import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroBriefcase, heroFlag, heroPauseCircle, heroUserPlus } from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { conversationPriorityEnum, type ConversationPriorityEnumKey } from '../../../../core/api/generated/models/ConversationPriority';
import type { CrmPipelineWithStages } from '../../../../core/api/generated/models/CrmPipelineWithStages';
import { IfPermissionDirective } from '../../../../core/auth/permission.directive';
import {
  ToastrService,
  UiBackdropButton,
  UiButton,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiDetailDrawer,
  UiIconButton,
  UiMenuItem,
  UiMenuPanel,
  UiMenuSeparator,
} from '../../../../shared/ui';
import { CrmApiService } from '../../../crm/crm-api.service';
import { ContactLinkActionsComponent } from '../../../crm/shared/contact-link/contact-link-actions.component';
import { IniciarConversaButtonComponent } from '../iniciar-conversa/iniciar-conversa-button.component';
import { DealFormComponent } from '../../../crm/pages/oportunidades/deal-form.component';
import { ConversationFiltersStore } from '../../data/conversation-filters.store';
import { ConversationsStore } from '../../data/conversations.store';
import { conversationPriorityLabel } from '../../i18n/conversation-labels';
import { snoozeUntilIso, type SnoozePreset } from '../../media/snooze-presets';
import { AssigneePickerModalComponent } from './assignee-picker-modal';

/** Header action cluster: assignee picker, snooze, lifecycle menu, resolve, create deal. */
@Component({
  selector: 'app-thread-header-actions',
  imports: [
    ContactLinkActionsComponent,
    IniciarConversaButtonComponent,
    NgIcon,
    UiBackdropButton,
    UiButton,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiIconButton,
    NgpMenuTrigger,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
    IfPermissionDirective,
    AssigneePickerModalComponent,
    UiDetailDrawer,
    DealFormComponent,
  ],
  providers: [provideIcons({ heroBriefcase, heroFlag, heroPauseCircle, heroUserPlus })],
  templateUrl: './thread-header-actions.html',
  styleUrls: ['./thread-header-actions.css', '../conversation-atoms.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreadHeaderActionsComponent {
  protected readonly store = inject(ConversationsStore);
  protected readonly filters = inject(ConversationFiltersStore);
  private readonly crmApi = inject(CrmApiService);
  private readonly toast = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  /** Contato da conversa selecionada — alimenta os atalhos de ligação/canal do cabeçalho. */
  protected readonly contactId = computed(() => this.store.selectedConversation()?.contactId ?? null);
  protected readonly contactName = computed(() => this.store.selectedConversation()?.contact ?? '');

  private readonly snoozeTrigger = viewChild<NgpMenuTrigger>('snoozeTrigger');
  private readonly moreTrigger = viewChild<NgpMenuTrigger>('moreTrigger');
  protected readonly assigneeOpen = signal(false);
  protected readonly confirmArchiveOpen = signal(false);
  protected readonly priorities = Object.values(conversationPriorityEnum);

  // Create-opportunity drawer state. Pipelines load lazily on first open and are
  // cached for the component's lifetime (pipelinesLoaded guards even an empty list).
  protected readonly dealDrawerOpen = signal(false);
  protected readonly loadingPipelines = signal(false);
  protected readonly pipelinesLoaded = signal(false);
  protected readonly pipelines = signal<CrmPipelineWithStages[]>([]);
  // Snapshot of the contact at open time — bound into the deal form. Snapshotting
  // (instead of a live getter) keeps a typed title from being wiped if the selected
  // conversation changes while the drawer is open.
  protected readonly dealContactId = signal<string | null>(null);

  openAssigneePicker(): void {
    this.assigneeOpen.set(true);
    this.closeMenus();
  }

  closeAssigneePicker(): void {
    this.assigneeOpen.set(false);
  }

  closeMenus(): void {
    this.moreTrigger()?.hide();
    this.snoozeTrigger()?.hide();
  }

  resolve(): void {
    this.store.resolve();
  }

  reopen(): void {
    this.store.reopen();
    this.closeMenus();
  }

  changePriority(priority: ConversationPriorityEnumKey): void {
    this.store.changePriority(priority);
    this.closeMenus();
  }

  priorityLabel(priority: ConversationPriorityEnumKey): string {
    return conversationPriorityLabel(priority);
  }

  requestArchive(): void {
    this.confirmArchiveOpen.set(true);
    this.closeMenus();
  }

  confirmArchive(): void {
    this.store.archive();
    this.confirmArchiveOpen.set(false);
  }

  snoozePreset(preset: SnoozePreset): void {
    this.store.snooze(snoozeUntilIso(preset));
    this.closeMenus();
  }

  /** Default pipeline for a new deal: the one flagged default, else the first. */
  get defaultPipelineId(): string | null {
    const list = this.pipelines();
    return (list.find((p) => p.pipeline.isDefault) ?? list[0])?.pipeline.id ?? null;
  }

  openCreateDeal(): void {
    this.closeMenus();
    this.dealContactId.set(this.store.selectedConversation()?.contactId ?? null);
    if (this.pipelinesLoaded()) {
      this.dealDrawerOpen.set(true);
      return;
    }
    this.loadingPipelines.set(true);
    this.crmApi
      .listPipelines()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pipelines) => {
          this.pipelines.set(pipelines);
          this.pipelinesLoaded.set(true);
          this.loadingPipelines.set(false);
          this.dealDrawerOpen.set(true);
        },
        error: () => {
          this.loadingPipelines.set(false);
          this.toast.error('Não foi possível carregar os funis.');
        },
      });
  }

  /** Deal-form already surfaces the success toast; just close the drawer. */
  onDealSaved(): void {
    this.dealDrawerOpen.set(false);
  }

  closeDealDrawer(): void {
    this.dealDrawerOpen.set(false);
  }
}
